import React, { createContext, useContext, useState, ReactNode, JSX } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  FiCheckCircle,
  FiInfo,
  FiAlertTriangle,
  FiXCircle,
} from "react-icons/fi";

type ToastType = "success" | "error" | "info" | "warning";

interface Toast {
  id: string;
  message: string;
  type: ToastType;
  duration?: number;
}

interface ToastContextType {
  showToast: (toast: Omit<Toast, "id">) => void;
}

const ToastContext = createContext<ToastContextType | null>(null);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) throw new Error("useToast must be used within ToastProvider");
  return context;
};

// Bootstrap colors (override if needed)
const typeStyles: Record<ToastType, { icon: JSX.Element; color: string }> = {
  success: { icon: <FiCheckCircle />, color: "#198754" }, // Bootstrap green
  error: { icon: <FiXCircle />, color: "#dc3545" }, // Bootstrap red
  info: { icon: <FiInfo />, color: "#0dcaf0" }, // Bootstrap cyan
  warning: { icon: <FiAlertTriangle />, color: "#ffc107" }, // Bootstrap yellow
};

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = (toast: Omit<Toast, "id">) => {
    const id = Date.now().toString();
    const newToast: Toast = { id, ...toast };
    setToasts(prev => [...prev, newToast]);

    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, toast.duration ?? 4000);
  };

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div
        className="position-fixed top-0 start-50 translate-middle-x p-3"
        style={{ zIndex: 1060 }}
      >
        <AnimatePresence>
          {toasts.map(toast => {
            const { icon, color } = typeStyles[toast.type];
            return (
              <motion.div
                key={toast.id}
                initial={{ opacity: 0, y: -30, scale: 0.8 }}
                animate={{
                  opacity: 1,
                  y: 0,
                  scale: [1, 1.05, 0.98, 1.02, 1],
                }}
                exit={{ opacity: 0, y: -20, scale: 0.9 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                className="mb-3"
              >
                <div
                  className="d-inline-flex align-items-start gap-3 p-3 rounded-3 shadow-lg"
                  style={{
                    backgroundColor: "var(--bs-body-bg, #212529)",
                    color: "var(--bs-body-color, #f8f9fa)",
                    border: `1px solid ${color}`,
                    maxWidth: "90vw",
                    width: "fit-content",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  <div style={{ color }}>{icon}</div>
                  <div style={{ flex: 1 }}>{toast.message}</div>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
};
